import { Alert, Platform } from 'react-native';

export type CenteredAlertButton = {
  text: string;
  onPress?: () => void;
  style?: 'default' | 'cancel' | 'destructive';
};

/**
 * Shows a centered modal on web instead of the browser's native alert (which often appears at the top).
 */
export function showCenteredAlert(
  title: string,
  message: string,
  buttons: CenteredAlertButton[] = [{ text: 'OK' }],
): void {
  if (Platform.OS !== 'web') {
    Alert.alert(title, message, buttons);
    return;
  }

  try {
    const win = globalThis as typeof globalThis & { document?: Document };
    const doc = win.document;
    if (!doc?.body) {
      Alert.alert(title, message, buttons);
      return;
    }

    const overlay = doc.createElement('div');
    overlay.setAttribute('data-bookapt-centered-alert', '1');
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '2147483646',
      backgroundColor: 'rgba(15, 23, 42, 0.55)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    });

    const panel = doc.createElement('div');
    Object.assign(panel.style, {
      backgroundColor: '#fff',
      borderRadius: '16px',
      padding: '24px',
      maxWidth: '420px',
      width: '100%',
      boxShadow: '0 24px 80px rgba(0, 0, 0, 0.28)',
      textAlign: 'left',
    });

    const titleEl = doc.createElement('h2');
    titleEl.textContent = title;
    Object.assign(titleEl.style, {
      margin: '0 0 12px 0',
      fontSize: '20px',
      fontWeight: '700',
      color: '#0f172a',
      lineHeight: '1.3',
    });

    const messageEl = doc.createElement('p');
    messageEl.textContent = message;
    Object.assign(messageEl.style, {
      margin: '0 0 20px 0',
      fontSize: '15px',
      color: '#475569',
      lineHeight: '1.5',
      whiteSpace: 'pre-wrap',
    });

    const actions = doc.createElement('div');
    Object.assign(actions.style, {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
      flexWrap: 'wrap',
    });

    const dismiss = (button?: CenteredAlertButton) => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      button?.onPress?.();
    };

    buttons.forEach((button, index) => {
      const btn = doc.createElement('button');
      btn.type = 'button';
      btn.textContent = button.text;
      const isPrimary = index === buttons.length - 1;
      Object.assign(btn.style, {
        border: isPrimary ? 'none' : '1px solid #CBD5E1',
        borderRadius: '10px',
        padding: '10px 18px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        backgroundColor: isPrimary ? '#667eea' : '#fff',
        color: isPrimary ? '#fff' : '#334155',
      });
      btn.addEventListener('click', () => dismiss(button));
      actions.appendChild(btn);
    });

    panel.appendChild(titleEl);
    panel.appendChild(messageEl);
    panel.appendChild(actions);
    overlay.appendChild(panel);
    doc.body.appendChild(overlay);
  } catch {
    Alert.alert(title, message, buttons);
  }
}
